import React from "react";

import cx from "classnames";

import { getLoginProviderText } from "~/auth/components/AuthButton";
import { ReactComponent as FellowWordmark } from "~/legacy-assets/images/logos/fellow-wordmark-black-solid.svg";
import {
	type AuthPostMessage,
	type OAuthParams,
	promptCustomAuth,
	promptGoogleAuth,
	promptOffice365Auth,
	promptOktaAuth,
	promptOneLoginAuth,
} from "~/lib/auth";
import { loadScript } from "~/lib/hooks/useScript";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack, getCookiesOfInterest, getReferringEntities } from "~/lib/segment";
import { getPublicTemplateContent, hasPublicTemplateContent } from "~/public/Template";
import { Tooltip } from "~/ui";
import { ContactSupportLink } from "~/ui/legacyFellowIndex";

import { AuthBase } from "../AuthBase";
import { AuthButton, GuestInviteHint, LoginHint, Terms } from "../components";
import { ReactComponent as EmailSent } from "../images/email-sent.svg";
import { ReactComponent as TooltipImg } from "../images/tooltipMeetingToggles.svg";
import { TemplatePane, TemplateWithContentPane, TestimonialPane, WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";
import type { CaptivePortalResult } from "./CaptivePortal";
import { TOPIC_TAG_MAP } from "./utils";

import styles from "../css/Auth.module.css";

async function uploadNoteTemplateContent(content: string, globalKey: string): Promise<{ templateContentId: string }> {
	const form = new FormData();
	form.set("content", content);
	form.set("globalKey", globalKey);
	return fetch("/public/templates/upload/", {
		method: "POST",
		body: form,
		headers: {
			"x-csrftoken": window.CSRF_TOKEN,
			"x-requested-with": "XMLHttpRequest",
		},
	}).then(res => {
		return res.json();
	});
}

async function prepareParamsWithTemplateContent(params: OAuthParams): Promise<OAuthParams> {
	return new Promise(resolve => {
		const { template } = window.INITIAL_STATE;
		if (!template || !template.global_key) return resolve(params);
		const content = getPublicTemplateContent(template.global_key);
		if (!content) return resolve(params);
		return uploadNoteTemplateContent(JSON.stringify(content), template.global_key)
			.then(({ templateContentId }) => {
				resolve({ ...params, templateContentId });
			})
			.catch(() => {
				resolve(params);
			});
	});
}

interface SignupState {
	errorMessage: string | undefined;
	loading: boolean;
	hasClickedSignup: boolean;
	emailInput: string;
	emailSent: boolean;
	isTemplateWithContent: boolean;
}

export default class Signup extends AuthBase<AuthProps, SignupState> {
	documentTitle = "Signup";

	state: SignupState = {
		errorMessage: "",
		loading: false,
		hasClickedSignup: false,
		emailInput: "",
		emailSent: false,
		isTemplateWithContent: false,
	};

	componentDidMount() {
		super.componentDidMount();

		loadScript(window.CELLO_ATTRIBUTION_URL, { async: true });
		const searchParams = new URLSearchParams(window.location.search);
		const { template } = window.INITIAL_STATE;
		if (
			template &&
			searchParams.has("with_content") &&
			template.global_key &&
			hasPublicTemplateContent(template.global_key)
		) {
			this.setState({ isTemplateWithContent: true });
			const newURL = new URL(window.location.href);
			newURL.searchParams.set("customized_template", "true");
			this.props.history.replace(newURL.pathname + newURL.search);
		}
	}

	getPaneComponent() {
		const { subdomain, template } = window.INITIAL_STATE;

		if (subdomain) {
			return WhitelabelPane;
		}

		if (template && this.state.isTemplateWithContent) {
			return TemplateWithContentPane;
		}

		if (template?.external_image) {
			return TemplatePane;
		}

		return TestimonialPane;
	}

	getPaneProps() {
		const { subdomain } = window.INITIAL_STATE;

		if (subdomain) {
			return { account: this.context.account, bgStyle: "wave" as const };
		}
		return { bgStyle: "purple" as const };
	}

	signupWith = (
		authPrompt: (params: OAuthParams) => Promise<AuthPostMessage>,
		permissionsDenied: string,
		analyticsTag: string,
	): void => {
		const {
			provider,
			invite,
			invite_link: inviteLink,
			template,
			integration_tag: integrationTag,
			topic_tag: topicTag,
			meeting_recap: meetingRecap,
		} = window.INITIAL_STATE;

		let params: OAuthParams = {
			signup: "generic",
			accountID: this.context.account?.id,
			referrer: JSON.stringify(getReferringEntities()),
		};
		if (invite) params = { ...params, inviteCode: invite.code };
		if (inviteLink) params = { ...params, inviteLinkCode: inviteLink.code };
		if (template) params = { ...params, templateID: String(template.id) };
		if (integrationTag) params = { ...params, integrationTag };
		if (topicTag) params = { ...params, topicTag };
		if (meetingRecap) params = { ...params, recapKey: meetingRecap.key };

		const searchParams = new URLSearchParams(window.location.search);
		const streamInviteID = searchParams.get("stream_invite");
		if (streamInviteID) params.streamInviteID = streamInviteID;

		const redirectBackToDesktopOnLastOauthStep = searchParams.get("redirectBackToDesktopOnLastOauthStep");
		if (redirectBackToDesktopOnLastOauthStep) params.redirectBackToDesktopOnLastOauthStep = true;

		/* eslint-disable camelcase */
		const analyticsData = {
			feature: "Authentication",
			integration: analyticsTag.toLowerCase(),
			integration_initial_provider: provider,
			invite_code: params.inviteCode,
			invite_link_code: params.inviteLinkCode,
			variant: "auth_prompt",
			...getReferringEntities(),
			...getCookiesOfInterest(),
		};
		/* eslint-enable camelcase */

		this.setState({ errorMessage: "" });

		dispatchAnalyticsTrack("Signup Step Auth Button Clicked", analyticsData, {
			integrations: {
				FACEBOOK_PIXEL: true,
				"Facebook Pixel": true,
				LINKEDIN_INSIGHT_TAG: true,
				"Linkedin Insight Tag": true,
			},
		});

		prepareParamsWithTemplateContent(params)
			.then(authPrompt)
			.then(data => {
				dispatchAnalyticsTrack("Signup Step Completed", analyticsData);
				window.setTimeout(() => {
					if (data.signup?.redirect) {
						window.location.href = data.signup.redirect;
					} else if (data.signup?.next) {
						this.context.setAuthContext(data.signup);
						this.props.history.push(data.signup.next, data);
					} else if (data.next) {
						dispatchAnalyticsTrack("Signed Up", {
							is_account_created: true, // eslint-disable-line camelcase
							...analyticsData,
						});
						window.location.href = data.next;
					}
				}, 100);
			})
			// eslint-disable-next-line camelcase
			.catch(({ code, message, email, signed_email }: AuthPostMessage) => {
				if (code === permissionsDenied) {
					dispatchAnalyticsTrack("Signup Step Failed", {
						reason: "integration_permission_rejected",
						...analyticsData,
					});
				} else if (code === "mismatched_email") {
					dispatchAnalyticsTrack("Signup Step Failed", { reason: "mismatched_email", ...analyticsData });
					this.props.history.push("/auth/alias/", {
						/* eslint-disable camelcase */
						oauth_email: email,
						signed_oauth_email: signed_email,
						/* eslint-enable camelcase */
						...this.props.location.state,
					});
				} else {
					dispatchAnalyticsTrack("Signup Step Failed", analyticsData);
					const msg = `${message ?? "Signup error"}`;
					this.setState({ errorMessage: msg });
				}
			});
	};

	signupWithGoogle = (): void => {
		this.signupWith(promptGoogleAuth, "access_denied", "google");
	};

	signupWithOffice365 = (): void => {
		this.signupWith(promptOffice365Auth, "consent_required", "office365");
	};

	signupWithOkta = (): void => {
		this.signupWith(promptOktaAuth, "", "okta");
	};

	signupWithOneLogin = (): void => {
		this.signupWith(promptOneLoginAuth, "", "one_login");
	};

	signupWithCustomSSO = (): void => {
		this.signupWith(promptCustomAuth, "", "custom");
	};

	onInputValueChange = (e: React.SyntheticEvent<HTMLInputElement, Event>) => {
		this.setState({ emailInput: e.currentTarget.value });
	};

	onInputKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.sendEmail();
		}
	};

	clearInputValue = () => this.setState({ emailInput: "" });

	sendEmail = async () => {
		const { emailInput, loading } = this.state;
		if (!emailInput || loading) return;

		this.setState({ loading: true });

		const token = await recaptcha("signup_captive_portal");
		const analyticsData = { feature: "Authentication", ...getReferringEntities() };

		const data = new FormData();
		data.append("email", emailInput);
		data.append("referrer", JSON.stringify(getReferringEntities()));
		data.append("captcha_token", token);

		const response = await fetch("/auth/captive/email/", {
			method: "POST",
			body: data,
			headers: {
				"x-client-session-id": window.CLIENT_SESSION_ID,
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		}).catch(({ message }) => {
			dispatchAnalyticsTrack("Signup Page Captive Portal Failed", analyticsData);
			this.setState({ loading: false, errorMessage: message });
		});

		if (!response) return;
		const result: CaptivePortalResult = await response.json();

		if (result.success) {
			dispatchAnalyticsTrack("Signup Page Captive Portal Completed", analyticsData);
			this.setState({ loading: false, emailSent: true, errorMessage: undefined });
		} else {
			dispatchAnalyticsTrack("Signup Page Captive Portal Failed", analyticsData);
			this.setState({ loading: false, errorMessage: result.message });
		}
	};

	renderWordmark() {
		const searchParams = new URLSearchParams(window.location.search);

		if (this.state.errorMessage) {
			return null; // renderHeader is already renders the error message
		}

		const { topic_tag: topicTag } = window.INITIAL_STATE;

		if (topicTag === "oneononeebook") {
			return (
				<>
					<div className={styles.wordMarkWrapper}>
						<FellowWordmark className={styles.wordMark} />
					</div>
					<h3 style={{ margin: 0 }}>Purpose-built to make every meeting and 1:1 a productive one</h3>
				</>
			);
		}

		if (topicTag && topicTag in TOPIC_TAG_MAP) {
			return (
				<>
					<h3>{TOPIC_TAG_MAP[topicTag]} with</h3>
					<div className={styles.wordMarkWrapper}>
						<FellowWordmark className={styles.wordMark} />
					</div>
				</>
			);
		}

		return (
			<div className={cx(styles.wordMarkWrapper, styles.wordMarkWrapperHeader)}>
				{searchParams.has("g2_lp_referral") && (
					<h3>Make great meetings a part of your organization&apos;s culture with</h3>
				)}
				<FellowWordmark className={styles.wordMark} />
			</div>
		);
	}

	renderEmailSent() {
		return (
			<div>
				<EmailSent />
				<h2>Check your inbox</h2>
				<h5 className={styles.emailSent}>
					We sent you an email with links to signup to Fellow. If you experience difficulties,{" "}
					<ContactSupportLink message="Hi! I'm trying to signup from an in-app browser.">contact us</ContactSupportLink>
					.
				</h5>
			</div>
		);
	}

	renderSignupButtons() {
		const { provider, account } = window.INITIAL_STATE;

		let ssoSignupButton;
		if (account?.sso?.provider) {
			let onClick;
			if (account.sso.provider === "ONE_LOGIN") {
				onClick = this.signupWithOneLogin;
			} else if (account.sso.provider === "OKTA") {
				onClick = this.signupWithOkta;
			} else if (account.sso.provider === "CUSTOM") {
				onClick = this.signupWithCustomSSO;
			}
			ssoSignupButton = (
				<AuthButton onClick={onClick} provider={account.sso.provider} action="login">
					Sign up with {getLoginProviderText(account.sso)}
				</AuthButton>
			);
		}

		// Show SSO login button if account has SSO provider, but show ONLY SSO login button if it is exclusive
		if (account?.sso?.provider && account?.sso?.is_exclusive_auth) {
			return <div className={styles.authButtons}>{ssoSignupButton}</div>;
		} else {
			return (
				<div className={styles.authButtons}>
					{(!provider || provider === "google" || provider === "unknown") && (
						<AuthButton onClick={this.signupWithGoogle} provider="Google" action="signup">
							Continue with Google
						</AuthButton>
					)}

					{(!provider || provider === "office365" || provider === "unknown") && (
						<AuthButton onClick={this.signupWithOffice365} provider="Microsoft" action="signup">
							Continue with Microsoft
						</AuthButton>
					)}

					{ssoSignupButton && ssoSignupButton}
				</div>
			);
		}
	}

	renderStep() {
		const {
			invite,
			template,
			invite_link: inviteLink,
			guest_url: guestUrl,
			invite_account: inviteAccount,
			invite_sender_name: inviteSenderName,
		} = window.INITIAL_STATE;
		const searchParams = new URLSearchParams(window.location.search);
		const streamInviteID = searchParams.get("stream_invite");

		const tooltipContent = (
			<div className={styles.tooltipContainer}>
				<div>
					<div className={styles.tooltipTitle}>Automate your meeting notes</div>
					<div className={styles.tooltipContent}>
						<p>
							Fellow syncs your calendar so you can choose the meetings you want to transcribe and summarize
							automatically.
						</p>
						<p>Works with Google Meet, Microsoft Teams, and Zoom.</p>
					</div>
				</div>
				<div>
					<TooltipImg />
				</div>
			</div>
		);

		return (
			<>
				<div className={styles.authFlex}>
					{this.renderWordmark()}
					{template ? (
						<>
							<h2>Use this template in your next meeting now</h2>
							<h5>Sign up with your work email to get started</h5>
						</>
					) : (
						<>
							{invite && <h2>{invite.subject}</h2>}
							{inviteLink && <h2>👋 {inviteLink.user.name} has invited you join their team!</h2>}
							{!invite &&
								!inviteLink &&
								(streamInviteID ? (
									<h2>{inviteSenderName} wants to share notes with you for your meeting</h2>
								) : (
									<h2>The #1 AI Meeting Assistant</h2>
								))}
							<h5 className={styles.signupSubtext}>
								Continue with your&nbsp;
								<Tooltip maxWidth={503} className={styles.tooltip} content={tooltipContent}>
									<span className={styles.bold}>work calendar</span>
								</Tooltip>
								&nbsp;to start using Fellow
							</h5>
						</>
					)}

					{this.renderSignupButtons()}
					{guestUrl && inviteAccount ? (
						<GuestInviteHint guestUrl={guestUrl} inviteAccount={inviteAccount} />
					) : streamInviteID ? (
						<></>
					) : (
						<LoginHint />
					)}
				</div>
			</>
		);
	}

	renderFooter() {
		return <Terms />;
	}
}
